import { PRIVACY_CORE_CUSTOM_EVENT_NAMES, SERVER_CONTEXT_FRAME_NAMES, SERVER_PURPOSE_LIMITATION_NAMES, CONSENT_SELECTION_SOURCE, dispatchEvent, getPrivacyCoreEventName, getGpcEnable, NI_C_COOKIES, SQ_COOKIES, } from '@nike/privacy-shared-logic';
import Cookies from 'js-cookie';
import { checkRetrievingLimitationMemberFromStore, checkRetrievingLimitationGuestFromStore, getPurposeLimitationByName, } from './purposeLimitations';
import { getContextFrameByName } from './contextFrames';
import { privacyCookieModalSeenKey, allowedCountriesWithNikePrivacy, allowedCountriesWithCPRA, PERSIST_KEY, } from '../constants';
import { getConsentCookieRemovalAttributes } from '../helpers/resolveConsentCookieDomain';
import { getContextFramesGuestAction, getContextFramesMemberAction, resetContextFramesMemberAction, } from '../store/contextFrames/actions';
import { getPurposeLimitationsMemberAction, getPurposeLimitationsGuestAction, updatePurposeLimitationsMemberAction, updatePurposeLimitationsGuestAction, resetPurposeLimitationsMemberAction, resetLimitationsErrorMemberAction, resetLimitationsErrorGuestAction, } from '../store/purposeLimitations/actions';
import getContextFramesMemberState from '../store/selectors/getContextFramesMemberState';
import getPurposeLimitationsMemberState from '../store/selectors/getPurposeLimitationsMemberState';
import getPurposeLimitationsGuestState from '../store/selectors/getPurposeLimitationsGuestState';
import getContextFramesGuestState from '../store/selectors/getContextFramesGuestState';
export class PrivacyServices {
    constructor(store) {
        this.getLanguage = language => {
            switch (language) {
                case 'no-NO': {
                    return 'nb-NO';
                }
                case 'zh-Hant-TW': {
                    return 'zh-TW';
                }
                case 'es-LA': {
                    return 'es-MX';
                }
                default: {
                    return language;
                }
            }
        };
        this.getGuestId = async (webShellIdentity) => {
            const visitData = await webShellIdentity.getVisitData();
            if (!visitData)
                return;
            return visitData.visitor || visitData.visitorId;
        };
        this.getMemberId = async (webShellIdentity) => { var _a; return (_a = (await webShellIdentity.getUserProfile())) === null || _a === void 0 ? void 0 : _a.upmId; };
        this.getUserId = async (accessToken, webShellIdentity) => {
            const userId = accessToken
                ? await this.getMemberId(webShellIdentity)
                : await this.getGuestId(webShellIdentity);
            if (typeof userId === 'undefined') {
                throw new Error('Get user id error');
            }
            return userId;
        };
        this.checkIsEnabled = () => {
            var _a, _b, _c, _d;
            const privacyCoreEnabledCookie = Cookies.get('privacyCoreEnabled');
            const privacyCoreEnabledCountriesCookie = Cookies.get('privacyCoreEnabledCountries');
            const privacyCoreDisabledCountriesCookie = Cookies.get('privacyCoreDisabledCountries');
            const country = ((_d = (_c = (_b = (_a = window.webShellClient) === null || _a === void 0 ? void 0 : _a.locale) === null || _b === void 0 ? void 0 : _b.get) === null || _c === void 0 ? void 0 : _c.call(_b).country) === null || _d === void 0 ? void 0 : _d.toUpperCase()) || '';
            const parsedEnabledCountries = privacyCoreEnabledCountriesCookie && privacyCoreEnabledCountriesCookie.split(',');
            const parsedDisabledCountries = privacyCoreDisabledCountriesCookie && privacyCoreDisabledCountriesCookie.split(',');
            if (!country)
                return false;
            if (privacyCoreEnabledCookie)
                return privacyCoreEnabledCookie === 'true';
            if (parsedEnabledCountries && parsedEnabledCountries.includes(country))
                return true;
            if (parsedDisabledCountries && parsedDisabledCountries.includes(country))
                return false;
            return allowedCountriesWithNikePrivacy.includes(country);
        };
        this.checkIsCPRAEnabled = () => {
            var _a, _b, _c, _d;
            const cpraEnabledCookie = Cookies.get('cpraEnabled');
            if (cpraEnabledCookie)
                return cpraEnabledCookie === 'true';
            const country = ((_d = (_c = (_b = (_a = window.webShellClient) === null || _a === void 0 ? void 0 : _a.locale) === null || _b === void 0 ? void 0 : _b.get) === null || _c === void 0 ? void 0 : _c.call(_b).country) === null || _d === void 0 ? void 0 : _d.toUpperCase()) || '';
            return allowedCountriesWithCPRA.includes(country);
        };
        this.clearBrowserDataRelatedToPrivacy = () => {
            const consentCookieRemovalAttributes = getConsentCookieRemovalAttributes();
            [SQ_COOKIES, NI_C_COOKIES].forEach(cookieName => consentCookieRemovalAttributes.forEach(attributes => Cookies.remove(cookieName, attributes)));
            [
                PERSIST_KEY.PURPOSE_LIMITATIONS,
                PERSIST_KEY.CONTEXT_FRAMES,
                PERSIST_KEY.ROOT,
                privacyCookieModalSeenKey,
            ].forEach(keyToRemove => window.localStorage.removeItem(keyToRemove));
        };
        this.updateMemberGpcValues = async ({ accessToken, language, userExperience, memberLocation, userId, }) => {
            const state = this.store.getState();
            const inlineMemberContextFrame = getContextFramesMemberState(state).contextFrames.find(contextFrame => contextFrame.metadata.contextFrameName === SERVER_CONTEXT_FRAME_NAMES.WEB_MEMBER_SETTINGS);
            if (!inlineMemberContextFrame)
                throw new Error('Can not find context frame');
            const consentsToDisable = inlineMemberContextFrame.contexts.filter(context => context.purposeLimitationCategory ===
                SERVER_PURPOSE_LIMITATION_NAMES.TARGETING_BEHAVIORAL_EVENTS ||
                context.purposeLimitationCategory === SERVER_PURPOSE_LIMITATION_NAMES.TARGETING_HASHEDMATCH);
            if (!consentsToDisable.length)
                return Promise.resolve();
            const { purposeLimitations } = getPurposeLimitationsMemberState(state);
            const purposeLimitationsToUpdate = {
                contextFrameId: inlineMemberContextFrame.metadata.contextFrameId,
                contextFrameVersionId: inlineMemberContextFrame.metadata.contextFrameVersionId,
                contextFrameConfirmLabelShown: !!inlineMemberContextFrame.metadata.contextFrameConfirmLabel,
                consentSelectionSource: CONSENT_SELECTION_SOURCE.ADVERTISING_DATA_SHARING_GPC_SWITCH,
                consentSelections: consentsToDisable.reduce((accumulator, consent) => {
                    const purposeLimitation = purposeLimitations.find(purposeLimitation => purposeLimitation.category === consent.purposeLimitationCategory);
                    if (!purposeLimitation)
                        throw new Error('Corresponded PL does not exist');
                    if (!purposeLimitation.isDefault && !purposeLimitation.isEnabled)
                        return accumulator;
                    return [
                        ...accumulator,
                        {
                            purposeLimitationCategory: consent.purposeLimitationCategory,
                            consent: false,
                            level: consent.level,
                            consentSelectionControlType: consent.consentSelectionControlMapping.multiPermission[consent.level],
                        },
                    ];
                }, []),
            };
            if (purposeLimitationsToUpdate.consentSelections.length <= 0)
                return Promise.resolve();
            await this.setInteractionMember({
                accessToken,
                language,
                userExperience,
                memberLocation,
                contextFrameToUpdate: purposeLimitationsToUpdate,
                uniqueEventsPrefix: '_updatingMemberGpc',
                userId,
            });
            if (getPurposeLimitationsMemberState(state).error)
                throw new Error('Initialize GPC error');
            return Promise.resolve();
        };
        this.updateGuestGpcValues = async ({ guestLocation, }) => {
            const state = this.store.getState();
            const inlineGuestContextFrame = getContextFramesGuestState(state).contextFrames.find(contextFrame => contextFrame.metadata.contextFrameName === SERVER_CONTEXT_FRAME_NAMES.WEB_GUEST_SETTINGS);
            if (!inlineGuestContextFrame)
                throw new Error('Can not find context frame');
            const consentsToDisable = inlineGuestContextFrame.contexts.filter(context => context.purposeLimitationCategory ===
                SERVER_PURPOSE_LIMITATION_NAMES.TARGETING_BEHAVIORAL_EVENTS ||
                context.purposeLimitationCategory === SERVER_PURPOSE_LIMITATION_NAMES.TARGETING_HASHEDMATCH);
            if (!consentsToDisable.length)
                return Promise.resolve();
            const { purposeLimitations } = getPurposeLimitationsGuestState(state);
            const purposeLimitationsToUpdate = {
                contextFrameId: inlineGuestContextFrame.metadata.contextFrameId,
                contextFrameVersionId: inlineGuestContextFrame.metadata.contextFrameVersionId,
                contextFrameConfirmLabelShown: !!inlineGuestContextFrame.metadata.contextFrameConfirmLabel,
                consentSelectionSource: CONSENT_SELECTION_SOURCE.ADVERTISING_DATA_SHARING_GPC_SWITCH,
                consentSelections: consentsToDisable.reduce((accumulator, consent) => {
                    const purposeLimitation = purposeLimitations.find(purposeLimitation => purposeLimitation.category === consent.purposeLimitationCategory);
                    if (!purposeLimitation)
                        throw new Error('Corresponded PL does not exist');
                    if (!purposeLimitation.isDefault && !purposeLimitation.isEnabled)
                        return accumulator;
                    return [
                        ...accumulator,
                        {
                            purposeLimitationCategory: consent.purposeLimitationCategory,
                            consent: false,
                            level: consent.level,
                            consentSelectionControlType: consent.consentSelectionControlMapping.multiPermission[consent.level],
                        },
                    ];
                }, []),
            };
            if (purposeLimitationsToUpdate.consentSelections.length <= 0)
                return Promise.resolve();
            await this.setInteractionGuest(purposeLimitationsToUpdate, '_updatingGuestGpc', guestLocation);
            if (getPurposeLimitationsGuestState(state).error)
                throw new Error('Initialize GPC error');
            return Promise.resolve();
        };
        this.initializeMember = async ({ accessToken, userId, language, userExperience, memberLocation, }) => {
            const { userId: storedUserId } = getPurposeLimitationsMemberState(this.store.getState());
            if (storedUserId && storedUserId !== userId) {
                this.store.dispatch(resetPurposeLimitationsMemberAction());
                this.store.dispatch(resetContextFramesMemberAction());
            }
            await this.store.dispatch(getPurposeLimitationsMemberAction({
                accessToken,
                userId,
                userExperience,
                memberLocation,
            }));
            await this.store.dispatch(getContextFramesMemberAction({ language, userExperience, memberLocation }));
            const state = this.store.getState();
            const { error: purposeLimitationsError } = getPurposeLimitationsMemberState(state);
            const { error: contextFramesError } = getContextFramesMemberState(state);
            if (purposeLimitationsError || contextFramesError)
                throw new Error('Initialize member error');
            if (getGpcEnable(memberLocation))
                await this.updateMemberGpcValues({
                    accessToken,
                    language,
                    userExperience,
                    memberLocation,
                    userId,
                });
        };
        this.initializeGuest = async ({ userId, language, userExperience, guestLocation, }) => {
            await this.store.dispatch(getPurposeLimitationsGuestAction({ userId, userExperience, guestLocation }));
            await this.store.dispatch(getContextFramesGuestAction({ language, userExperience }));
            const state = this.store.getState();
            const guestPurposeLimitationsError = getPurposeLimitationsGuestState(state).error;
            const guestContextFramesError = getContextFramesGuestState(state).error;
            const hasMemberPurposeLimitations = !!Object.keys(state.purposeLimitations.member).length;
            const hasMemberContextFrames = !!Object.keys(state.contextFrames.member).length;
            if (hasMemberPurposeLimitations)
                this.store.dispatch(resetPurposeLimitationsMemberAction());
            if (hasMemberContextFrames)
                this.store.dispatch(resetContextFramesMemberAction());
            window.localStorage.removeItem(privacyCookieModalSeenKey);
            if (guestPurposeLimitationsError || guestContextFramesError)
                throw new Error('Initialize guest error');
            if (getGpcEnable(guestLocation))
                await this.updateGuestGpcValues({ guestLocation });
        };
        this.initialize = async ({ accessToken, userId, language, userExperience, memberLocation, guestLocation, }) => {
            return accessToken
                ? await this.initializeMember({
                    accessToken,
                    userId,
                    language,
                    userExperience,
                    memberLocation,
                })
                : await this.initializeGuest({
                    userId,
                    language,
                    userExperience,
                    guestLocation,
                });
        };
        this.getPermissionMember = name => {
            const canRetrieveLimitationFromStore = checkRetrievingLimitationMemberFromStore();
            if (!canRetrieveLimitationFromStore)
                throw new Error('Can not retrieve permissions');
            const { purposeLimitations } = getPurposeLimitationsMemberState(this.store.getState());
            return getPurposeLimitationByName(purposeLimitations, name);
        };
        this.getPermissionGuest = name => {
            const canRetrieveLimitationFromStore = checkRetrievingLimitationGuestFromStore();
            if (!canRetrieveLimitationFromStore)
                throw new Error('Can not retrieve permissions');
            const { purposeLimitations } = getPurposeLimitationsGuestState(this.store.getState());
            return getPurposeLimitationByName(purposeLimitations, name);
        };
        this.getPermissionsAllMember = () => {
            const canRetrieveLimitationFromStore = checkRetrievingLimitationMemberFromStore();
            if (!canRetrieveLimitationFromStore)
                throw new Error('Can not retrieve permissions');
            const { purposeLimitations } = getPurposeLimitationsMemberState(this.store.getState());
            return purposeLimitations;
        };
        this.getPermissionsAllGuest = () => {
            const canRetrieveLimitationFromStore = checkRetrievingLimitationGuestFromStore();
            if (!canRetrieveLimitationFromStore)
                throw new Error('Can not retrieve permissions');
            const { purposeLimitations } = getPurposeLimitationsGuestState(this.store.getState());
            return purposeLimitations;
        };
        this.getStoredGuestPermission = name => {
            const canRetrieveLimitationFromStore = checkRetrievingLimitationGuestFromStore();
            if (!canRetrieveLimitationFromStore)
                return;
            const { purposeLimitations } = getPurposeLimitationsGuestState(this.store.getState());
            return getPurposeLimitationByName(purposeLimitations, name);
        };
        this.getPermission = ({ name, accessToken }) => {
            return accessToken ? this.getPermissionMember(name) : this.getPermissionGuest(name);
        };
        this.getPermissionsAll = accessToken => {
            return accessToken ? this.getPermissionsAllMember() : this.getPermissionsAllGuest();
        };
        this.getInteractionGuest = () => {
            const canRetrieveLimitationFromStore = checkRetrievingLimitationGuestFromStore();
            const { contextFrames, error: contextFramesError } = getContextFramesGuestState(this.store.getState());
            if (!canRetrieveLimitationFromStore || contextFramesError)
                throw new Error('Get interaction error');
            return contextFrames;
        };
        this.getInteractionMember = () => {
            const canRetrieveLimitationFromStore = checkRetrievingLimitationMemberFromStore();
            const { contextFrames, error: contextFramesError } = getContextFramesMemberState(this.store.getState());
            if (!canRetrieveLimitationFromStore || contextFramesError)
                throw new Error('Get interaction error');
            return contextFrames;
        };
        this.getInteraction = ({ name, accessToken }) => {
            const contextFrames = accessToken ? this.getInteractionMember() : this.getInteractionGuest();
            if (contextFrames)
                return getContextFrameByName(contextFrames, name);
            return;
        };
        this.setInteractionGuest = async (contextFrameToUpdate, uniqueEventsPrefix, guestLocation, captureInitialConsent = false) => {
            dispatchEvent(getPrivacyCoreEventName(PRIVACY_CORE_CUSTOM_EVENT_NAMES.PERMISSIONS_UPDATING, uniqueEventsPrefix));
            await this.store.dispatch(updatePurposeLimitationsGuestAction(contextFrameToUpdate, !!captureInitialConsent, guestLocation));
            const { error: purposeLimitationsError } = getPurposeLimitationsGuestState(this.store.getState());
            if (purposeLimitationsError) {
                this.store.dispatch(resetLimitationsErrorGuestAction());
                throw new Error('Set interaction error');
            }
        };
        this.setInteractionMember = async ({ accessToken, language, userExperience, contextFrameToUpdate, uniqueEventsPrefix, memberLocation, userId, }) => {
            dispatchEvent(getPrivacyCoreEventName(PRIVACY_CORE_CUSTOM_EVENT_NAMES.PERMISSIONS_UPDATING, uniqueEventsPrefix));
            await this.store.dispatch(updatePurposeLimitationsMemberAction({
                accessToken,
                language,
                userExperience,
                contextFrameToUpdate,
                memberLocation,
                userId,
            }));
            const { error: purposeLimitationsError } = getPurposeLimitationsMemberState(this.store.getState());
            if (purposeLimitationsError) {
                this.store.dispatch(resetLimitationsErrorMemberAction());
                throw new Error('Set interaction error');
            }
            window.localStorage.setItem(privacyCookieModalSeenKey, 'true');
        };
        this.setInteraction = async ({ accessToken, language, userExperience, guestLocation, memberLocation, userId, contextFrameToUpdate, uniqueEventsPrefix, captureInitialConsent, }) => {
            return accessToken
                ? await this.setInteractionMember({
                    accessToken,
                    language,
                    userExperience,
                    contextFrameToUpdate,
                    uniqueEventsPrefix,
                    memberLocation,
                    userId,
                })
                : await this.setInteractionGuest(contextFrameToUpdate, uniqueEventsPrefix, guestLocation, captureInitialConsent);
        };
        this.store = store;
    }
}
